/**
 * Onboarding Modal
 * A 6-screen welcome wizard for new users
 *
 * Screens:
 * 1. Welcome - Value prop highlights
 * 2. What You Get - Free tier feature grid
 * 3. Sportsbook - Select preferred sportsbook
 * 4. Upgrade Preview - Tier highlights (Pro/Premium/Elite)
 * 5. Smart Betting Tips - Practical advice for responsible use
 * 6. Ready - Get Started CTA
 */

import { getOddsApiSportsbookConfigs } from '@config/constants';
import { useAuth } from '@contexts/AuthContext';
import {
  getOnboardingFreeFeatures,
  getOnboardingTierHighlights,
  ONBOARDING_BETTING_TIPS,
  ONBOARDING_VALUE_PROPS,
} from '@parlay-picker/shared/onboardingContent';
import { DEFAULT_SPORTSBOOK } from '@parlay-picker/shared/sportsbook';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronRight,
  ClipboardCheck,
  Crown,
  DollarSign,
  Eye,
  FileSearch,
  Hammer,
  Lightbulb,
  Package,
  ShieldCheck,
  TrendingUp,
  Trophy,
  Zap,
} from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';

interface OnboardingModalProps {
  onClose: () => void;
  onNavigateToPicks?: () => void;
}

// Map icon names from shared content to actual lucide components
const ICON_MAP: Record<string, typeof Zap> = {
  Lightbulb,
  TrendingUp,
  ShieldCheck,
  Zap,
  Package,
  ClipboardCheck,
  Trophy,
  BarChart3,
  Eye,
  Hammer,
  FileSearch,
};

// Gradient configs for value prop cards
const VALUE_PROP_GRADIENTS = [
  { gradient: 'from-blue-500 to-cyan-400', bgGradient: 'from-blue-500/20 to-cyan-400/10' },
  { gradient: 'from-emerald-500 to-green-400', bgGradient: 'from-emerald-500/20 to-green-400/10' },
  { gradient: 'from-violet-500 to-purple-400', bgGradient: 'from-violet-500/20 to-purple-400/10' },
];

// Gradient configs for tier cards
const TIER_GRADIENTS: Record<string, string> = {
  pro: 'from-orange-500 to-amber-400',
  premium: 'from-purple-500 to-violet-400',
  elite: 'from-indigo-500 to-blue-400',
};

export function OnboardingModal({
  onClose,
  onNavigateToPicks,
}: OnboardingModalProps): ReactElement {
  const { completeOnboarding, updatePreferences, user } = useAuth();
  const [step, setStep] = useState(1);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [selectedSportsbook, setSelectedSportsbook] = useState(
    user?.preferences?.preferredSportsbook || DEFAULT_SPORTSBOOK,
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'in' | 'out'>('in');
  const [isVisible, setIsVisible] = useState(false);

  const totalSteps = 6;
  const freeFeatures = getOnboardingFreeFeatures();
  const tierHighlights = getOnboardingTierHighlights();

  // Animate in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSkip = async (): Promise<void> => {
    if (dontShowAgain) {
      await completeOnboarding();
    }
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  const handleNext = async (): Promise<void> => {
    if (isAnimating || step >= totalSteps) return;

    // Save sportsbook when leaving step 3
    if (step === 3 && selectedSportsbook !== user?.preferences?.preferredSportsbook) {
      await updatePreferences({ preferredSportsbook: selectedSportsbook });
    }

    setIsAnimating(true);
    setSlideDirection('out');

    setTimeout(() => {
      setStep(step + 1);
      setSlideDirection('in');
      setTimeout(() => setIsAnimating(false), 300);
    }, 200);
  };

  const handleFinish = async (generatePicks: boolean): Promise<void> => {
    await completeOnboarding();
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      if (generatePicks && onNavigateToPicks) {
        onNavigateToPicks();
      }
    }, 200);
  };

  const sportsbooksList = getOddsApiSportsbookConfigs();

  const getSlideClasses = (): string => {
    if (!isAnimating && slideDirection === 'in') return 'translate-x-0 opacity-100';
    if (slideDirection === 'out') return '-translate-x-4 opacity-0';
    return 'translate-x-4 opacity-0';
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop with gradient */}
      <div className="absolute inset-0 bg-linear-to-br from-gray-900/95 via-gray-900/98 to-black/95 backdrop-blur-md" />

      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-theme-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      {/* Modal */}
      <div
        className={`relative bg-linear-to-b from-gray-800 to-gray-850 rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col border border-theme-border/50 shadow-2xl overflow-hidden transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Header gradient bar */}
        <div className="h-1 w-full bg-linear-to-r from-blue-500 via-purple-500 to-cyan-500" />

        {/* Skip button */}
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-5 right-5 text-theme-text-secondary hover:text-white transition-colors duration-200 text-sm font-medium flex items-center gap-1 group"
        >
          Skip
          <ChevronRight className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" />
        </button>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6">
          <div className={`transition-all duration-200 ease-out ${getSlideClasses()}`}>
            {/* Step 1: Welcome */}
            {step === 1 && (
              <div className="text-center">
                {/* Logo/Icon */}
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-linear-to-br from-blue-500 to-purple-600 rounded-2xl blur-xl opacity-50" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Welcome to{' '}
                  <span className="bg-linear-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    Parlay Picker
                  </span>
                </h2>
                <p className="text-theme-text-secondary mb-8 text-lg">
                  Basketball props, explained
                </p>

                {/* Value prop cards */}
                <div className="space-y-3 text-left">
                  {ONBOARDING_VALUE_PROPS.map((prop, index) => {
                    const Icon = ICON_MAP[prop.iconName];
                    const colors = VALUE_PROP_GRADIENTS[index];
                    return (
                      <div
                        key={prop.title}
                        className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-[1.02]"
                      >
                        <div
                          className={`absolute inset-0 bg-linear-to-r ${colors.bgGradient} opacity-50 group-hover:opacity-70 transition-opacity`}
                        />
                        <div className="absolute inset-0 bg-theme-surface/50" />

                        <div className="relative flex items-start gap-4 p-4">
                          <div
                            className={`w-12 h-12 rounded-xl bg-linear-to-br ${colors.gradient} flex items-center justify-center shrink-0 shadow-lg`}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>

                          <div className="pt-1">
                            <h3 className="font-semibold text-white text-lg mb-0.5">
                              {prop.title}
                            </h3>
                            <p className="text-sm text-theme-text-secondary leading-relaxed">
                              {prop.description}
                            </p>
                          </div>
                        </div>

                        <div className="absolute inset-0 rounded-xl border border-theme-border/50 group-hover:border-theme-border/50 transition-colors" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: What You Get (Free Tier) */}
            {step === 2 && (
              <div className="text-center">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-linear-to-br from-emerald-500 to-green-400 rounded-full blur-xl opacity-40" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-emerald-500 to-green-400 rounded-full flex items-center justify-center shadow-lg">
                    <Check className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.5} />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Your free plan includes
                </h2>
                <p className="text-theme-text-secondary mb-6 text-lg">
                  Everything you need to get started
                </p>

                <div className="grid grid-cols-2 gap-2.5 text-left">
                  {freeFeatures.map((feature) => {
                    const Icon = ICON_MAP[feature.iconName];
                    return (
                      <div
                        key={feature.label}
                        className="flex items-center gap-2.5 bg-theme-surface/60 border border-theme-border/40 rounded-lg px-3 py-2.5"
                      >
                        <Icon className="w-4 h-4 text-theme-success shrink-0" />
                        <span className="text-sm text-gray-200">{feature.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Sportsbook */}
            {step === 3 && (
              <div className="text-center">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-linear-to-br from-emerald-500 to-green-400 rounded-2xl blur-xl opacity-40" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-emerald-500 to-green-400 rounded-2xl flex items-center justify-center shadow-lg">
                    <DollarSign className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Which sportsbook do you use?
                </h2>
                <p className="text-theme-text-secondary mb-8 text-lg">
                  We'll show odds from your book
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {sportsbooksList.map((book) => {
                    const isSelected = selectedSportsbook === book.id;
                    return (
                      <button
                        type="button"
                        key={book.id}
                        onClick={() => setSelectedSportsbook(book.id)}
                        className={`relative group p-4 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-theme-primary bg-theme-primary/10 scale-[1.02]'
                            : 'border-theme-border bg-theme-surface/50 hover:border-theme-border hover:bg-theme-surface-hover/50'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-theme-primary-fill rounded-full flex items-center justify-center shadow-lg">
                            <Check className="w-3 h-3 text-theme-on-primary-fill" strokeWidth={3} />
                          </div>
                        )}

                        <div className="flex flex-col items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${book.dot} shadow-sm`} />
                          <span
                            className={`text-sm font-medium transition-colors ${
                              isSelected
                                ? 'text-white'
                                : 'text-theme-text-secondary group-hover:text-white'
                            }`}
                          >
                            {book.name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-theme-text-tertiary mt-4">
                  You can change this anytime in settings
                </p>
              </div>
            )}

            {/* Step 4: Upgrade Preview */}
            {step === 4 && (
              <div className="text-center">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-linear-to-br from-amber-500 to-orange-400 rounded-2xl blur-xl opacity-40" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-amber-500 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg">
                    <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Unlock more</h2>
                <p className="text-theme-text-secondary mb-6 text-lg">
                  Upgrade anytime from settings
                </p>

                <div className="space-y-3 text-left">
                  {tierHighlights.map((tier) => {
                    const gradient = TIER_GRADIENTS[tier.tier];
                    return (
                      <div
                        key={tier.tier}
                        className="relative overflow-hidden rounded-xl border border-theme-border/50"
                      >
                        <div className={`absolute inset-0 bg-linear-to-r ${gradient} opacity-5`} />
                        <div className="relative p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`font-bold text-lg bg-linear-to-r ${gradient} bg-clip-text text-transparent`}
                            >
                              {tier.name}
                            </span>
                            <span className="text-sm font-semibold text-theme-text-secondary">
                              {tier.price}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {tier.features.map((feature) => (
                              <div key={feature} className="flex items-center gap-1.5">
                                <Check className="w-3 h-3 text-theme-success shrink-0" />
                                <span className="text-xs text-theme-text-secondary">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5: Smart Betting Tips */}
            {step === 5 && (
              <div className="text-center">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-linear-to-br from-amber-500 to-yellow-400 rounded-2xl blur-xl opacity-40" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-amber-500 to-yellow-400 rounded-2xl flex items-center justify-center shadow-lg">
                    <ShieldCheck className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Bet smart</h2>
                <p className="text-theme-text-secondary mb-6 text-lg">
                  A few tips before you start
                </p>

                <div className="space-y-3 text-left">
                  {ONBOARDING_BETTING_TIPS.map((tip, index) => {
                    const Icon = ICON_MAP[tip.iconName];
                    const colors = VALUE_PROP_GRADIENTS[index];
                    return (
                      <div
                        key={tip.title}
                        className="group relative overflow-hidden rounded-xl transition-all duration-300"
                      >
                        <div
                          className={`absolute inset-0 bg-linear-to-r ${colors.bgGradient} opacity-50`}
                        />
                        <div className="absolute inset-0 bg-theme-surface/50" />

                        <div className="relative flex items-start gap-4 p-4">
                          <div
                            className={`w-12 h-12 rounded-xl bg-linear-to-br ${colors.gradient} flex items-center justify-center shrink-0 shadow-lg`}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>

                          <div className="pt-1">
                            <h3 className="font-semibold text-white text-base mb-0.5">
                              {tip.title}
                            </h3>
                            <p className="text-sm text-theme-text-secondary leading-relaxed">
                              {tip.description}
                            </p>
                          </div>
                        </div>

                        <div className="absolute inset-0 rounded-xl border border-theme-border/50 transition-colors" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 6: Ready */}
            {step === 6 && (
              <div className="text-center">
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 bg-linear-to-br from-green-500 to-emerald-400 rounded-full blur-xl opacity-40 animate-pulse" />
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-linear-to-br from-green-500 to-emerald-400 rounded-full flex items-center justify-center shadow-xl">
                    <Check className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.5} />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">You're all set!</h2>
                <p className="text-theme-text-secondary mb-8">
                  Start generating AI-powered parlay picks
                </p>

                <button
                  type="button"
                  onClick={() => handleFinish(true)}
                  className="group w-full py-4 bg-linear-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  Generate Your First Picks
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  type="button"
                  onClick={() => handleFinish(false)}
                  className="w-full mt-3 py-3 text-theme-text-secondary hover:text-white text-sm font-medium transition-colors"
                >
                  Explore the app
                </button>

                {/* Celebration particles */}
                <div
                  className="absolute top-20 left-8 w-2 h-2 bg-theme-warning rounded-full opacity-60 animate-bounce"
                  style={{ animationDelay: '0.1s' }}
                />
                <div
                  className="absolute top-32 right-12 w-1.5 h-1.5 bg-theme-primary rounded-full opacity-60 animate-bounce"
                  style={{ animationDelay: '0.3s' }}
                />
                <div
                  className="absolute top-24 right-20 w-2 h-2 bg-theme-success rounded-full opacity-60 animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer with progress and controls */}
        <div className="shrink-0 px-4 sm:px-8 py-4 sm:py-5 border-t border-theme-border/50 bg-theme-surface/50">
          <div className="flex items-center justify-between">
            {/* Don't show again checkbox */}
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-theme-text-secondary hover:text-theme-text-secondary transition-colors select-none group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-5 h-5 rounded border-2 border-theme-border bg-theme-surface-hover/50 peer-checked:bg-theme-primary-fill peer-checked:border-blue-600 transition-all duration-200 flex items-center justify-center group-hover:border-gray-500">
                  {dontShowAgain && (
                    <Check className="w-3 h-3 text-theme-on-primary-fill" strokeWidth={3} />
                  )}
                </div>
              </div>
              Don't show again
            </label>

            {/* Progress dots and next button */}
            <div className="flex items-center gap-5">
              {/* Progress dots */}
              <div className="flex gap-2">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map((i) => (
                  <div
                    key={i}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === step
                        ? 'w-6 bg-linear-to-r from-blue-500 to-purple-500'
                        : i < step
                          ? 'w-2 bg-theme-primary'
                          : 'w-2 bg-theme-surface-hover'
                    }`}
                  />
                ))}
              </div>

              {/* Next button (not shown on last step) */}
              {step < totalSteps && (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isAnimating}
                  className="group px-5 py-2.5 bg-linear-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
